/**
 * Centralized credit costs for all NimbleNode features.
 *
 * This is the single source of truth. All components that need credit cost
 * information should import from here instead of defining local copies.
 *
 * Keys MUST match exactly what the backend writes to
 * CreditTransaction.feature_used via
 * deductCredits(base44, email, cost, description). See
 * base44/functions/[name]/entry.ts for the canonical strings.
 */

// Feature credit costs — keys must match backend feature_used strings exactly.
// Verified against:
//   base44/functions/geminiGenerate/entry.ts:148      -> "image generation with gemini-flash-image" | "image generation with gemini-pro-image"
//   base44/functions/geminiThumbnail/entry.ts:122     -> "Thumbnail generation"
//   base44/functions/geminiEdit/entry.ts:136          -> "Image edit: <operation>"
//   base44/functions/googleVeoGenerate/entry.ts:570   -> "Video Generation (Veo)"
//   base44/functions/heygenGenerate/entry.ts:190      -> "AI Avatar Video"
//   base44/functions/elevenLabsGenerate/entry.ts:378  -> "Voice Generation"
//   base44/functions/elevenLabsClone/entry.ts:352     -> "Voice Clone"
//   base44/functions/analyzeCompetitor/entry.ts:335   -> "Competitor Analysis"
export const FEATURE_CREDITS = {
  // Image generation (Gemini) — geminiGenerate/entry.ts
  'image generation with gemini-flash-image': 5,
  'image generation with gemini-pro-image': 15,

  // Thumbnail (Gemini) — geminiThumbnail/entry.ts
  'Thumbnail generation': 15,

  // Image editing (Gemini) — geminiEdit/entry.ts, keys are "Image edit: <operation>"
  'Image edit: inpaint': 5,
  'Image edit: outpaint': 4,
  'Image edit: remove_object': 5,
  'Image edit: remove_background': 5,
  'Image edit: search_recolor': 5,
  'Image edit: search_replace': 5,
  'Image edit: replace_background_relight': 8,
  'Image edit: structure': 5,
  'Image edit: sketch': 5,
  'Image edit: style_guide': 5,
  'Image edit: style_transfer': 8,

  // Video generation (Veo) — googleVeoGenerate/entry.ts
  // Variable cost: baseCosts[duration] × multipliers[quality]
  //   duration: 4→240, 6→400, 8→640
  //   quality:  draft×1, standard×1.5, premium×2
  //   range:    240..1280
  // A representative value (standard 6s = 400 × 1.5 = 600) is stored so direct
  // lookups return a number. Use `VIDEO_VEO_COST_RANGE` / `getFeatureCost` for
  // accurate per-request ranges.
  'Video Generation (Veo)': 600,

  // Avatar video (HeyGen) — heygenGenerate/entry.ts
  'AI Avatar Video': 50,

  // Voice generation (ElevenLabs TTS) — elevenLabsGenerate/entry.ts
  // Variable cost: Math.ceil((wordCount / 150) * 30) ≈ 30 credits per ~150 words
  // (~1 minute of speech). Baseline stored for direct lookup.
  'Voice Generation': 30,

  // Voice cloning (ElevenLabs) — elevenLabsClone/entry.ts
  'Voice Clone': 600,

  // Competitor analysis — analyzeCompetitor/entry.ts
  'Competitor Analysis': 10,

  // Video render (Polotno Cloud Render) — renderVideo/entry.ts
  // Variable cost: 100 base for <=15s, +10 per extra 5s block
  'Video Render (Polotno)': 100,
};

/**
 * Ranges for features whose backend cost is computed per-request rather than
 * being a flat constant. UI components that display cost ranges should read
 * from here instead of hard-coding numbers.
 */
export const VIDEO_VEO_COST_RANGE = { min: 240, max: 1280 };
export const VIDEO_POLOTNO_BASE_COST = 100;
export const VIDEO_POLOTNO_EXTRA_PER_5S = 10;
export const VOICE_GENERATION_COST_PER_MINUTE = 30;

/**
 * Look up the credit cost for a backend feature_used string.
 *
 * For flat-rate features this returns a number. For variable-cost features
 * (Video Generation, Voice Generation) it returns an object so callers can
 * decide how to render them.
 *
 * @param {string} featureUsed exact string the backend wrote to CreditTransaction.feature_used
 * @returns {number | { min: number, max: number, perMinute?: boolean } | undefined}
 */
export function getFeatureCost(featureUsed) {
  if (featureUsed === 'Video Generation (Veo)') {
    return { ...VIDEO_VEO_COST_RANGE };
  }
  if (featureUsed === 'Video Render (Polotno)') {
    return { min: VIDEO_POLOTNO_BASE_COST, max: VIDEO_POLOTNO_BASE_COST + 60 };
  }
  if (featureUsed === 'Voice Generation') {
    return {
      min: VOICE_GENERATION_COST_PER_MINUTE,
      max: VOICE_GENERATION_COST_PER_MINUTE,
      perMinute: true,
    };
  }
  return FEATURE_CREDITS[featureUsed];
}

/**
 * Editing-specific costs keyed by operation slug.
 * Used by ImageEditor and other edit UI components.
 * Mirrors EDITING_COSTS in base44/functions/geminiEdit/entry.ts.
 */
export const EDITING_COSTS = {
  inpaint: FEATURE_CREDITS['Image edit: inpaint'],
  outpaint: FEATURE_CREDITS['Image edit: outpaint'],
  remove_object: FEATURE_CREDITS['Image edit: remove_object'],
  remove_background: FEATURE_CREDITS['Image edit: remove_background'],
  search_recolor: FEATURE_CREDITS['Image edit: search_recolor'],
  search_replace: FEATURE_CREDITS['Image edit: search_replace'],
  replace_background_relight: FEATURE_CREDITS['Image edit: replace_background_relight'],
  structure: FEATURE_CREDITS['Image edit: structure'],
  sketch: FEATURE_CREDITS['Image edit: sketch'],
  style_guide: FEATURE_CREDITS['Image edit: style_guide'],
  style_transfer: FEATURE_CREDITS['Image edit: style_transfer'],
};

/**
 * Actions that cost ZERO credits (non-AI operations).
 * These are always free for all subscribers.
 */
export const FREE_ACTIONS = [
  'scheduling',
  'analytics',
  'calendar',
  'settings',
  'marketplace_browsing',
  'team_management',
];
